/**
 * Reports > Packaging (user, 2026-10-22): the legacy SeedTrac Packaging module's reports (wmsd
 * packging/index1.php, read view-only on 2026-10-13, 2026-10-22 and 2026-10-30; docs/reference/
 * seedtrac-packaging-live-2026-10-13.md §5A), in its menu order. Since 2026-10-30 ("copy reports as
 * it is") each has the legacy result page's columns, labels and filters, no more and no fewer. Each returns the shared
 * `ReportResult`, so the screen table, Excel and PDF are the RSW reports' own builders.
 *
 * Two of them pivot by pack size (one Qty column per UPS the period or stock holds): their columns
 * come back on the result (`ReportResult.columns`), the definition carries only the fixed ones.
 */
import type {
  ReportCellFormat,
  ReportColumn,
  ReportDef,
} from "./warehouse-reports";

export const PACKAGING_REPORT_SLUGS = [
  "stock",
  "daily-qc",
  "qc-ageing",
  "activity",
  "variety-wise",
  "packing",
  "ivt",
] as const;
export type PackagingReportSlug = (typeof PACKAGING_REPORT_SLUGS)[number];
export type PackagingReportKey = `pack-${PackagingReportSlug}`;

export const isPackagingReportSlug = (s: string): s is PackagingReportSlug =>
  (PACKAGING_REPORT_SLUGS as readonly string[]).includes(s);
export const packagingReportKey = (
  slug: PackagingReportSlug
): PackagingReportKey => `pack-${slug}`;

/** The legacy "Pack QC Status" choice: lots packed on a QC test date, on a soft release, or both. */
export const PACK_QC_BASES = ["dot", "dosf"] as const;
export type PackQcBasis = (typeof PACK_QC_BASES)[number];

/** The legacy QC Ageing "Select" durations, in days since the last QC test. */
export const PACK_AGE_BANDS = {
  lt45: { label: "Less than 45 days", min: null, max: 44 },
  "45-90": { label: "45 to 90 days", min: 45, max: 90 },
  gt90: { label: "More than 90 days", min: 91, max: null },
} as const;
export type PackAgeBand = keyof typeof PACK_AGE_BANDS;
/** The legacy QC Ageing "Fill" comparison: less than, equal to or more than N days. */
export const PACK_AGE_COMPARE = {
  lt: "Less than",
  eq: "Equal to",
  gt: "More than",
} as const;
export type PackAgeCompare = keyof typeof PACK_AGE_COMPARE;

/** Periodical Packing: one row per packed lot and UPS, or one per crop, variety, UPS and pack type. */
export const PACKING_VIEWS = ["detail", "summary"] as const;
export type PackingView = (typeof PACKING_VIEWS)[number];

const c = (
  key: string,
  label: string,
  format: ReportCellFormat,
  more: Partial<Omit<ReportColumn, "key" | "label" | "format">> = {}
): ReportColumn => ({
  key,
  label,
  format,
  align:
    format === "int" || format === "qty" || format === "pct"
      ? "right"
      : format === "text"
        ? "left"
        : "center",
  ...more,
});

const SERIAL = c("serial", "#", "int", { align: "center" });
const CROP = c("crop", "Crop", "text");
const VARIETY = c("variety", "Variety", "text");
const LOT = c("lotNumber", "Lot No.", "text");
const UPS = c("ups", "UPS", "text", { align: "center" });
/** Pack seed sits in the bins as pouches (stock bags = pouches, D12). */
const NOP = c("pouches", "NoP", "int", { total: true });
const QTY = c("qty", "Qty", "qty", { total: true });
const GERM = c("germinationPct", "Germ %", "pct");
const QC = c("qcStatus", "QC Status", "text", { align: "center" });
const GOT = c("gotStatus", "GOT Status", "text", { align: "center" });
const DOGR = c("dogr", "DOGR", "date");
const SLOC = c("slocs", "SLOC", "slocs");
const LOTS_TOO_MANY = "Too many lots. Choose a crop or variety.";
const PERIOD_TOO_MANY = "Too many rows. Shorten the period or choose a crop.";
const GOT_NOTE =
  "GOT status is the reviewed GOT result where the lot's lineage has one, else the status declared at arrival.";
/** A packed lot's QC is its lineage's: a pouch is tested as the condition lot it was packed from. */
const LINEAGE_NOTE =
  "Latest QC is the latest result on the packed lot or the lots it was packed from.";

/** The pivot's fixed head and tail: the UPS columns go between them (`ReportResult.columns`). */
export const PACK_PIVOT_HEAD: readonly ReportColumn[] = [SERIAL, CROP, VARIETY];
export const PACK_PIVOT_TAIL: readonly ReportColumn[] = [
  c("qty", "Total Qty", "qty", { total: true }),
];
/** One pivot column: the qty packed (or in stock) in this pack size, keyed `ups:<packSizeId>`. */
export const packSizeColumn = (packSizeId: number, label: string) =>
  c(`ups:${packSizeId}`, label, "qty", { total: true });

export const PACKAGING_REPORTS: {
  [K in PackagingReportKey]: ReportDef & { key: K };
} = {
  "pack-stock": {
    key: "pack-stock",
    menu: "Pack Seed Stock Report",
    title: "Pack Seed Stock Report",
    asOn: true,
    kind: "sectioned",
    filters: ["crop", "variety", "ups", "packQc"],
    // Legacy: Lot No., Size, NoMP, Qty, …; master packs are gone (0165), so NoP stands in NoMP's place.
    columns: [
      SERIAL,
      LOT,
      c("ups", "Size", "text", { align: "center" }),
      NOP,
      QTY,
      c("packQcStatus", "Pack QC status", "text", { align: "center" }),
      c("packQcOn", "Pack DoT", "date"),
      c("qcStatus", "Latest QC status", "text", { align: "center" }),
      c("testedOn", "Latest DoT", "date"),
      GERM,
      c("validUpto", "DoV", "date"),
      c("validityDays", "Validity (in Days)", "int"),
      SLOC,
    ],
    tooMany: LOTS_TOO_MANY,
    note: `${LINEAGE_NOTE} DoV is the Valid upto the lot was packed with; Validity is the days left to it, negative once it has passed. A lot packed in more than one UPS has its stock split across them in the packed proportion.`,
  },
  "pack-daily-qc": {
    key: "pack-daily-qc",
    menu: "Daily QC Result Report",
    title: "Daily QC Result Report",
    asOn: false,
    kind: "flat",
    filters: ["date"],
    columns: [
      SERIAL,
      CROP,
      VARIETY,
      LOT,
      QTY,
      c("ppVerdict", "PP", "text", { align: "center" }),
      c("moisturePct", "Moist %", "pct"),
      GERM,
      c("testedOn", "DOT", "date"),
      QC,
      DOGR,
      GOT,
    ],
    tooMany: "Too many results on one day. Choose a crop.",
    note: `QC results recorded on pack seed (Pack stage samples) on this day. ${GOT_NOTE}`,
  },
  "pack-qc-ageing": {
    key: "pack-qc-ageing",
    menu: "QC Ageing Status Report",
    title: "QC Test Ageing Status Report",
    asOn: true,
    kind: "sectioned",
    filters: ["crop", "variety", "packAge"],
    // Legacy repeats Crop / Variety in columns under its "Crop: · Variety:" heading; the heading of
    // each Crop | Variety block carries them here, as on every sectioned report.
    columns: [
      SERIAL,
      LOT,
      c("pouches", "NoB", "int", { total: true }),
      QTY,
      c("stage", "Stage", "text", { align: "center" }),
      c("ppVerdict", "PP", "text", { align: "center" }),
      c("moisturePct", "Moist %", "pct"),
      GERM,
      c("testedOn", "DOT", "date"),
      c("daysSinceDot", "Days since DoT", "int"),
      QC,
      DOGR,
      GOT,
      SLOC,
    ],
    tooMany: LOTS_TOO_MANY,
    note: `Pack seed in stock whose last QC result is OK. ${LINEAGE_NOTE} ${GOT_NOTE}`,
  },
  "pack-activity": {
    key: "pack-activity",
    menu: "Pack Seed Activity Report",
    title: "Periodical Packed Seed Activity Report",
    asOn: false,
    kind: "aggregated",
    filters: ["period", "crop", "variety", "ups"],
    columns: [...PACK_PIVOT_HEAD, ...PACK_PIVOT_TAIL],
    tooMany: PERIOD_TOO_MANY,
    note: "Seed packed in the period, by UPS, from submitted Packing Slips and Processing & Packing Slips. Lots whose QC has failed are left out.",
  },
  "pack-variety-wise": {
    key: "pack-variety-wise",
    menu: "Variety wise Pack Seed Report",
    title: "Crop Variety Wise Packed Seed Report",
    asOn: true,
    kind: "aggregated",
    filters: ["crop", "variety", "ups"],
    columns: [...PACK_PIVOT_HEAD, ...PACK_PIVOT_TAIL],
    tooMany: LOTS_TOO_MANY,
    note: "Pack seed in stock by UPS. Lots whose QC has failed are left out. A lot packed in more than one UPS has its stock split across them in the packed proportion.",
  },
  "pack-packing": {
    key: "pack-packing",
    menu: "Periodical Packing Report",
    title: "Periodical Packing Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety", "ups", "withReprint", "packingView"],
    columns: [
      SERIAL,
      c("packedOn", "Date", "date"),
      CROP,
      VARIETY,
      c("lotNumber", "Lot Number", "text"),
      UPS,
      c("upstype", "Type", "text", { align: "center" }),
      c("pickedQty", "Picked for Packing Qty", "qty", { total: true }),
      c("packLoss", "Packing Loss", "qty", { total: true }),
      NOP,
      c("qty", "Total Qty", "qty", { total: true }),
    ],
    tooMany: PERIOD_TOO_MANY,
    note: "This is NOT a Stock Report. It covers only the Packing Activity of the selected period. Lots are accumulated irrespective of their QC status (RT/OK/UT/FAIL). A lot packed in more than one UPS carries its picked qty and loss on its first UPS row.",
  },
  "pack-ivt": {
    key: "pack-ivt",
    menu: "Periodical IVT Report",
    title: "Crop Variety wise IVT Report",
    asOn: false,
    kind: "flat",
    filters: ["period", "crop", "variety"],
    // Legacy report_ivt1.php: no # column. Its title reads "Crop Variety wise Packing Loss Report"
    // (a copy-paste slip there), so ours keeps the IVT title. Its With SLOC / With Harvest & Arrival
    // Date radios are never sent to the result page (2026-10-30), so they are not copied.
    columns: [
      c("date", "Date", "date"),
      c("fromCrop", "From Crop", "text"),
      c("fromVariety", "From Variety", "text"),
      c("fromLot", "From Lotno", "text"),
      c("fromBags", "NoB", "int", { total: true }),
      c("fromQty", "Qty", "qty", { total: true }),
      c("toCrop", "To Crop", "text"),
      c("toVariety", "To Variety", "text"),
      c("toLot", "To Lotno", "text"),
      c("toBags", "NoB", "int", { total: true }),
      c("toQty", "Qty", "qty", { total: true }),
    ],
    tooMany: PERIOD_TOO_MANY,
    note: "Inter Variety Transfers submitted in the period. Crop and Variety filter on the From side.",
  },
};

export interface PackagingReportEntry {
  slug: PackagingReportSlug;
  key: PackagingReportKey;
  menu: string;
  hint: string;
}

const HINT: Record<PackagingReportSlug, string> = {
  stock:
    "Pack seed in stock by lot and UPS, with pack QC, latest QC, DoV and validity days",
  "daily-qc": "QC results recorded on pack seed on one day",
  "qc-ageing": "QC-OK pack seed by days since its last test",
  activity: "Seed packed in a period, crop and variety by UPS",
  "variety-wise": "Pack seed in stock now, crop and variety by UPS",
  packing:
    "Lots packed in a period: picked, loss, pouches and qty; summary or lot-wise",
  ivt: "Inter Variety Transfers in a period, from lot to new lot",
};

/** The legacy Packaging Reports menu, in order, with what each tile says. */
export const PACKAGING_REPORT_LIST: readonly PackagingReportEntry[] =
  PACKAGING_REPORT_SLUGS.map((slug) => {
    const key = packagingReportKey(slug);
    return { slug, key, menu: PACKAGING_REPORTS[key].menu, hint: HINT[slug] };
  });
